import React, { useState, useEffect } from 'react';
import { Plus } from 'lucide-react';
import { getCostCenters, createCostCenter } from '../../../api/erp';
import { toast } from 'react-toastify';

const WholesaleCostCentersPage = () => {
  const [centers, setCenters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState({ code: '', name: '', type: 'cost', description: '' });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const data = await getCostCenters({ module: 'wholesale' });
      setCenters(data);
    } catch (error) {
      toast.error('Failed to load cost centers');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await createCostCenter({ ...formData, module: 'wholesale' });
      toast.success('Created successfully');
      setShowModal(false);
      fetchData();
    } catch (error) {
      toast.error('Failed to create');
    }
  };

  return (
    <div className="p-6">
      <div className="flex justify-between mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Wholesale Cost & Profit Centers</h1>
        <button 
          onClick={() => setShowModal(true)} 
          className="flex items-center gap-2 text-white px-4 py-2 rounded transition-colors"
          style={{ backgroundColor: 'var(--wholesale-primary, #047857)' }}
        >
          <Plus size={16} /> New Center
        </button>
      </div>

      <div className="bg-white rounded shadow overflow-hidden border border-gray-100">
        <table className="w-full">
          <thead className="bg-gray-50 text-gray-700 uppercase text-xs">
            <tr>
              <th className="px-6 py-3 text-left">Code</th>
              <th className="px-6 py-3 text-left">Name</th>
              <th className="px-6 py-3 text-left">Type</th>
              <th className="px-6 py-3 text-left">Description</th>
            </tr>
          </thead>
          <tbody className="text-sm">
            {centers.length > 0 ? (
              centers.map(c => (
                <tr key={c.id} className="border-t border-gray-100 hover:bg-gray-50">
                  <td className="px-6 py-4 font-mono text-emerald-700">{c.code}</td>
                  <td className="px-6 py-4 font-medium text-gray-900">{c.name}</td>
                  <td className="px-6 py-4">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      c.type === 'cost' ? 'bg-orange-100 text-orange-700' : 'bg-blue-100 text-blue-700'
                    }`}>
                      {c.type === 'cost' ? 'Cost Center' : 'Profit Center'}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-gray-500">{c.description || '-'}</td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="4" className="px-6 py-8 text-center text-gray-500">
                  {loading ? 'Loading centers...' : 'No wholesale centers found'}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md border border-gray-100">
            <h2 className="text-xl font-bold mb-4 text-gray-900">New Wholesale Center</h2>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Code *</label>
                <input 
                  placeholder="e.g. WS-ADM" 
                  className="w-full border border-gray-300 p-2 rounded focus:ring-2 focus:ring-emerald-500 focus:border-transparent outline-none"
                  value={formData.code}
                  onChange={e => setFormData({...formData, code: e.target.value})}
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
                <input 
                  placeholder="e.g. Wholesale Administration" 
                  className="w-full border border-gray-300 p-2 rounded focus:ring-2 focus:ring-emerald-500 focus:border-transparent outline-none"
                  value={formData.name}
                  onChange={e => setFormData({...formData, name: e.target.value})}
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Type *</label>
                <select 
                  className="w-full border border-gray-300 p-2 rounded focus:ring-2 focus:ring-emerald-500 focus:border-transparent outline-none"
                  value={formData.type}
                  onChange={e => setFormData({...formData, type: e.target.value})}
                >
                  <option value="cost">Cost Center</option>
                  <option value="profit">Profit Center</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea 
                  placeholder="Describe the purpose of this center" 
                  className="w-full border border-gray-300 p-2 rounded focus:ring-2 focus:ring-emerald-500 focus:border-transparent outline-none"
                  value={formData.description}
                  onChange={e => setFormData({...formData, description: e.target.value})}
                  rows="3"
                />
              </div>
              <div className="flex gap-2 justify-end pt-4 border-t border-gray-100">
                <button 
                  type="button" 
                  onClick={() => setShowModal(false)} 
                  className="px-4 py-2 border border-gray-300 rounded hover:bg-gray-50 text-gray-700 transition-colors"
                >
                  Cancel
                </button>
                <button 
                  type="submit" 
                  className="px-4 py-2 text-white rounded transition-colors"
                  style={{ backgroundColor: 'var(--wholesale-primary, #047857)' }}
                >
                  Create
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default WholesaleCostCentersPage;
